import { User } from '../types';

const API_URL = (import.meta.env.VITE_API_URL || '') + '/api/auth';

// Debug helpers removed for security
const debugRequest = (url: string, options: RequestInit) => {
  // No-op in production
};

const debugResponse = async (response: Response, url: string) => {
  return response;
};

// In-memory token storage (more secure than localStorage)
let accessToken: string | null = null;
let tokenExpiry: number | null = null;

// Token refresh timer
let refreshTimer: NodeJS.Timeout | null = null;

// Refresh token promise for handling concurrent refresh attempts
let refreshTokenPromise: Promise<void> | null = null;

// Get current access token
export const getToken = (): string | null => {
  if (accessToken && tokenExpiry && Date.now() < tokenExpiry) {
    return accessToken;
  }
  return null;
};

// Set access token with expiry
const setToken = (token: string, expiresIn: string = '15m'): void => {
  accessToken = token;
  const expiryMs = parseExpiry(expiresIn);
  tokenExpiry = Date.now() + expiryMs;

  // Set up automatic refresh 2 minutes before expiry
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  const refreshTime = Math.max(expiryMs - 2 * 60 * 1000, 30 * 1000); // At least 30 seconds
  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(console.error);
  }, refreshTime);
};

// Parse expiry string to milliseconds
const parseExpiry = (expiresIn: string): number => {
  const match = expiresIn.match(/^(\d+)([smhd])$/);
  if (!match) return 15 * 60 * 1000; // Default 15 minutes

  const value = parseInt(match[1]);
  const unit = match[2];

  switch (unit) {
    case 's': return value * 1000;
    case 'm': return value * 60 * 1000;
    case 'h': return value * 60 * 60 * 1000;
    case 'd': return value * 24 * 60 * 60 * 1000;
    default: return 15 * 60 * 1000;
  }
};

// Clear token and timer
const clearToken = (): void => {
  accessToken = null;
  tokenExpiry = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

// Make authenticated request with automatic token refresh
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getToken();

  const requestOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  debugRequest(url, requestOptions);
  let response = await fetch(url, requestOptions);
  response = await debugResponse(response, url);

  // If token expired, try to refresh (but only once, avoid infinite loops)
  if (response.status === 401 && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
    try {
      // If a refresh is already in progress, wait for it
      if (refreshTokenPromise) {
        await refreshTokenPromise;
      } else {
        // Start a new refresh
        await refreshAccessToken();
      }

      const newToken = getToken();

      if (newToken) {
        // Retry the original request with new token (only once)
        const retryOptions: RequestInit = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            Authorization: `Bearer ${newToken}`,
          },
        };
        debugRequest(url, retryOptions);
        response = await fetch(url, retryOptions);
        response = await debugResponse(response, url);
      }
    } catch (error) {
      // Refresh failed, user needs to login again
      clearToken();
      throw new Error('Session expired. Please log in again.');
    }
  }

  return response;
};

// Refresh access token using refresh token from cookie
export const refreshAccessToken = async (): Promise<void> => {
  // If refresh is already in progress, return the existing promise
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  // Create new refresh promise
  refreshTokenPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Refresh token failed:', response.status, errorText);
        throw new Error('Failed to refresh token');
      }

      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Non-JSON refresh response:', responseText);
        throw new Error('Server returned invalid response format');
      }

      const data = await response.json();
      if (data.token) {
        setToken(data.token);
      }
    } catch (error) {
      console.error('Refresh token error:', error);
      clearToken();
      throw error;
    } finally {
      // Clear the promise so next refresh can proceed
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

// Get current user info
export const getCurrentUser = async (): Promise<User> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/me`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Get user failed:', response.status, errorText);
      throw new Error('Failed to get user info');
    }

    // Check if response has content before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('Non-JSON user response:', responseText);
      throw new Error('Server returned invalid response format');
    }

    return response.json();
  } catch (error) {
    console.error('Get current user error:', error);
    throw error;
  }
};

// Register new user
export const register = async (username: string, email: string, password: string, referralCode?: string): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, email, password, referralCode }),
    });

    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to register');
      } else {
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
    }

    // Parse response only once
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('Non-JSON response:', responseText);
      throw new Error('Server returned invalid response format');
    }

    return await response.json();
  } catch (error) {
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      console.error('JSON parse error:', error);
      throw new Error('Server is not responding properly. Please check if the backend is running.');
    }
    throw error;
  }
};

// Login user
export const login = async (email: string, password: string): Promise<{ token: string; user: User }> => {
  try {
    const requestOptions = {
      method: 'POST',
      credentials: 'include' as RequestCredentials,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    };

    debugRequest(`${API_URL}/login`, requestOptions);
    const response = await fetch(`${API_URL}/login`, requestOptions);
    await debugResponse(response, `${API_URL}/login`);

    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to log in');
      } else {
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
    }

    // Parse response only once
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('Non-JSON response:', responseText);
      throw new Error('Server returned invalid response format');
    }

    const data = await response.json();
    if (data.token) {
      setToken(data.token);
    }
    return data;
  } catch (error) {
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      console.error('JSON parse error:', error);
      throw new Error('Server is not responding properly. Please check if the backend is running.');
    }
    throw error;
  }
};

// Login with Google
export const loginWithGoogle = async (token: string, referralCode?: string): Promise<{ token: string; user: User }> => {
  try {
    const requestOptions = {
      method: 'POST',
      credentials: 'include' as RequestCredentials,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, referralCode }),
    };

    debugRequest(`${API_URL}/google`, requestOptions);
    const response = await fetch(`${API_URL}/google`, requestOptions);
    await debugResponse(response, `${API_URL}/google`);

    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to log in with Google');
      } else {
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
    }

    // Parse response only once
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('Non-JSON response:', responseText);
      throw new Error('Server returned invalid response format');
    }

    const data = await response.json();
    if (data.token) {
      setToken(data.token);
    }
    return data;
  } catch (error) {
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      console.error('JSON parse error:', error);
      throw new Error('Server is not responding properly. Please check if the backend is running.');
    }
    throw error;
  }
};

// Logout user
export const logout = async (): Promise<void> => {
  try {
    await authenticatedFetch(`${API_URL}/logout`, {
      method: 'POST',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearToken();
  }
};

// MFA Setup
export const setupMFA = async (tempToken?: string): Promise<{ secret: string; qrCode: string }> => {
  try {
    const options: RequestInit = {};
    if (tempToken) {
      options.headers = { Authorization: `Bearer ${tempToken}` };
    }

    const response = await authenticatedFetch(`${API_URL}/mfa/setup`, {
      method: 'POST',
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to setup MFA');
    }

    return await response.json();
  } catch (error) {
    console.error('MFA setup error:', error);
    throw error;
  }
};

// MFA Verify (Enable)
export const verifyMFA = async (token: string, tempToken?: string): Promise<{ message: string; backupCodes?: string[]; token?: string; user?: User }> => {
  try {
    const options: RequestInit = {
      method: 'POST',
      body: JSON.stringify({ token }),
    };

    if (tempToken) {
      options.headers = { Authorization: `Bearer ${tempToken}` };
    }

    const response = await authenticatedFetch(`${API_URL}/mfa/verify`, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to verify MFA');
    }

    const data = await response.json();
    if (data.token) {
      setToken(data.token);
    }
    return data;
  } catch (error) {
    console.error('MFA verify error:', error);
    throw error;
  }
};

// MFA Validate (Login)
export const validateMFA = async (tempToken: string, code: string): Promise<{ token: string; user: User }> => {
  try {
    const response = await fetch(`${API_URL}/mfa/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tempToken, code }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invalid MFA code');
    }

    const data = await response.json();
    if (data.token) {
      // Use the internal setToken function if possible, but it's not exported.
      // We need to handle token setting here or in the context.
      // Since setToken is not exported, we'll rely on the caller (AuthContext) to handle the user state,
      // but we really should set the token for subsequent requests.
      // Let's modify the file to export setToken or just duplicate the logic?
      // Duplicating logic is bad.
      // Ideally, validateMFA should just return data and AuthContext handles it.
      // But authApiClient manages the token state.
      // I will modify the setToken function to be exported or use a workaround.
      // Actually, I can just call the internal setToken if I'm inside the module.
      // Wait, I am editing the file, so I can just call setToken(data.token).
      // Yes, I am appending to the file, so I have access to setToken.
      setToken(data.token);
    }
    return data;
  } catch (error) {
    console.error('MFA validate error:', error);
    throw error;
  }
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return getToken() !== null;
};

// Update Profile
export const updateProfile = async (data: { username?: string; profilePicture?: string }): Promise<User> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/profile`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update profile');
    }

    return await response.json();
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};

// Change Password
export const changePassword = async (data: { oldPassword: string; newPassword: string }): Promise<void> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/change-password`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to change password');
    }
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
};

// Forgot Password
export const forgotPassword = async (email: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to request password reset');
    }
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

// Reset Password
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to reset password');
    }
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

// Delete Account
export const deleteAccount = async (password: string): Promise<void> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/account`, {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete account');
    }

    clearToken();
  } catch (error) {
    console.error('Delete account error:', error);
    throw error;
  }
};

// Verify Email
export const verifyEmail = async (token: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to verify email');
    }
  } catch (error) {
    console.error('Verify email error:', error);
    throw error;
  }
};

// Resend Verification Email
export const resendVerificationEmail = async (email: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to resend verification email');
    }
  } catch (error) {
    console.error('Resend verification error:', error);
    throw error;
  }
};

export interface UserSession {
  id: string;
  device_fingerprint: string;
  last_active: string;
  created_at: string;
  ip_address?: string;
  user_agent?: string;
  is_current?: boolean;
}

export const getSessions = async (): Promise<UserSession[]> => {
  const response = await authenticatedFetch(`${API_URL}/sessions`);
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch sessions');
  }
  return await response.json();
};

export const revokeSession = async (sessionId: string): Promise<void> => {
  const response = await authenticatedFetch(`${API_URL}/sessions/${sessionId}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to revoke session');
  }
};